import { useLanguage } from '../i18n';
import type { InterviewExtract, Goal } from '../types/interview';

interface InterviewExtractViewProps {
  extract: InterviewExtract;
}

function GoalItem({ goal, depth = 0 }: { goal: Goal; depth?: number }) {
  return (
    <li className={`border-l-2 ${depth === 0 ? 'border-red-300' : 'border-cloud'} pl-3 py-1.5`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-3">
        <div className="min-w-0">
          <div className="font-medium text-ink text-sm">{goal.title}</div>
          {goal.rationale && <div className="text-xs text-slate mt-0.5">{goal.rationale}</div>}
          {goal.done_criteria && (
            <div className="text-xs text-ash mt-0.5">
              <span className="font-medium">Done:</span> {goal.done_criteria}
            </div>
          )}
        </div>
        <span
          className={`shrink-0 self-start text-xs px-2 py-0.5 font-medium ${
            goal.status === 'done'
              ? 'bg-emerald-50 text-emerald-700'
              : goal.status === 'in_progress'
              ? 'bg-amber-50 text-amber-700'
              : 'bg-mist text-slate'
          }`}
        >
          {goal.status}
        </span>
      </div>
      {goal.sub_goals && goal.sub_goals.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {goal.sub_goals.map((g) => (
            <GoalItem key={g.id} goal={g} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  if (!value || !value.trim()) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-slate uppercase tracking-wider mb-1">{label}</div>
      <div className="text-sm text-ink leading-relaxed">{value}</div>
    </div>
  );
}

function ListField({ label, items }: { label: string; items: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-slate uppercase tracking-wider mb-1">{label}</div>
      <ul className="list-disc list-inside text-sm text-ink space-y-0.5">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

export default function InterviewExtractView({ extract }: InterviewExtractViewProps) {
  const { t } = useLanguage();
  const i = t.interview;
  return (
    <div className="bg-white border border-cloud p-4 sm:p-5 h-full overflow-y-auto space-y-4">
      <h3 className="text-sm font-semibold text-slate uppercase tracking-wider">{i.extractTitle}</h3>
      <Field label={i.extractTopic} value={extract.topic} />
      <Field label={i.extractPurpose} value={extract.purpose} />
      <Field label={i.extractProblem} value={extract.problem} />
      <Field label={i.extractSolution} value={extract.solution} />
      <Field label={i.extractScope} value={extract.scope} />
      <div>
        <div className="text-xs font-semibold text-slate uppercase tracking-wider mb-1">{i.extractGoals}</div>
        {extract.goals.length === 0 ? (
          <p className="text-sm text-ash italic">{i.extractNoGoals}</p>
        ) : (
          <ul className="space-y-1.5">
            {extract.goals.map((g) => (
              <GoalItem key={g.id} goal={g} />
            ))}
          </ul>
        )}
      </div>
      <ListField label={i.extractRisks} items={extract.risks} />
      <ListField label={i.extractNextSteps} items={extract.next_steps} />
    </div>
  );
}
